import { useRef } from "react";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import {
  AsyncSearchableSelect,
  type SearchableOption,
} from "@/components/SearchableSelect";
import { ticketTemplates as templatesApi } from "@/features/tickets/api/ticketTemplates.api";
import { useCanInsertTemplate } from "@/features/tickets/templatePermissions";

const PAGE_SIZE = 20;

export function TemplateInsert({
  onInsert,
  disabled = false,
  className,
}: {
  onInsert: (content: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const canInsert = useCanInsertTemplate();
  const contents = useRef(new Map<string, string>());

  if (!canInsert) return null;

  async function search({ keyword, pageNumber }: { keyword: string; pageNumber: number }) {
    const page = await templatesApi.list({ keyword, pageNumber, pageSize: PAGE_SIZE });
    const options: SearchableOption[] = page.items.map((t) => {
      contents.current.set(t.id, t.content);
      return { value: t.id, label: t.name };
    });
    return {
      options,
      hasNextPage: pageNumber * PAGE_SIZE < page.totalCount,
      totalCount: page.totalCount,
    };
  }

  function pick(id: string) {
    const content = contents.current.get(id);
    if (content !== undefined) onInsert(content);
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <Label className="flex items-center gap-1.5 text-sm font-normal text-slate-600">
        <FileText className="h-4 w-4 shrink-0 text-slate-400" />
        Insert a template
      </Label>
      <AsyncSearchableSelect
        value=""
        onValueChange={pick}
        search={search}
        disabled={disabled}
        placeholder="Choose…"
        searchPlaceholder="Search templates…"
        emptyText="No templates yet"
        className="w-64"
      />
    </div>
  );
}
